import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';

interface PagePermission {
  page: string;
  displayName: string;
  actions: Array<{
    action: string;
    label: string;
    description?: string;
  }>;
}

interface SchemaFlag {
  key: string;
  label: string;
  description?: string;
  ui?: {
    disablesTenantAccessList?: boolean;
  };
}

interface PermissionEditorProps {
  permissions: Record<string, Record<string, boolean>>;
  flags: Record<string, boolean>;
  tenantAccess: string[];
  availableTenants: string[];
  onChange: (data: {
    permissions: Record<string, Record<string, boolean>>;
    flags: any;
    tenantAccess: string[];
  }) => void;
}

export function PermissionEditor({
  permissions,
  flags,
  tenantAccess,
  availableTenants,
  onChange,
}: PermissionEditorProps) {
  const [localPermissions, setLocalPermissions] = useState(permissions);
  const [localFlags, setLocalFlags] = useState(flags);
  const [localTenantAccess, setLocalTenantAccess] = useState(tenantAccess);
  const [pagePermissions, setPagePermissions] = useState<PagePermission[]>([]);
  const [schemaFlags, setSchemaFlags] = useState<SchemaFlag[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);

  useEffect(() => {
    const loadSchema = async () => {
      try {
        const response = await apiClient.getPermissionSchema();
        const schemaPages = response.schema?.pages || {};
        const schemaFlagsRaw = response.schema?.flags || {};
        const pages: PagePermission[] = Object.entries(schemaPages).map(([page, config]: [string, any]) => ({
          page,
          displayName: config.displayName || page,
          actions: (config.actions || []).map((a: any) => ({
            action: a.action,
            label: a.label || a.action,
            description: a.description,
          })),
        }));
        const flagsList: SchemaFlag[] = Object.entries(schemaFlagsRaw).map(([key, cfg]: [string, any]) => ({
          key,
          label: cfg?.label || key,
          description: cfg?.description,
          ui: cfg?.ui,
        }));
        setPagePermissions(pages);
        setSchemaFlags(flagsList);
      } catch (error) {
        console.error('Failed to load permission schema:', error);
        // Fallback to empty array - UI will show nothing but won't crash
        setPagePermissions([]);
        setSchemaFlags([]);
      } finally {
        setIsLoadingSchema(false);
      }
    };
    loadSchema();
  }, []);

  const handlePermissionToggle = (page: string, action: string) => {
    const updated = { ...localPermissions };
    if (!updated[page]) {
      updated[page] = {};
    }
    updated[page][action] = !updated[page][action];
    setLocalPermissions(updated);
    onChange({ permissions: updated, flags: localFlags, tenantAccess: localTenantAccess });
  };

  const handleFlagToggle = (flag: string) => {
    const updated = { ...localFlags, [flag]: !localFlags[flag] };
    setLocalFlags(updated);
    onChange({ permissions: localPermissions, flags: updated, tenantAccess: localTenantAccess });
  };

  const handleTenantToggle = (tenant: string) => {
    const updated = localTenantAccess.includes(tenant)
      ? localTenantAccess.filter((t) => t !== tenant)
      : [...localTenantAccess, tenant];
    setLocalTenantAccess(updated);
    onChange({ permissions: localPermissions, flags: localFlags, tenantAccess: updated });
  };

  const handlePageAllToggle = (page: string) => {
    const pageConfig = pagePermissions.find((p) => p.page === page);
    if (!pageConfig) return;

    const updated = { ...localPermissions };
    if (!updated[page]) {
      updated[page] = {};
    }

    // Check if all actions are currently enabled
    const allEnabled = pageConfig.actions.every((action) => updated[page][action.action]);

    // Toggle all actions
    pageConfig.actions.forEach((action) => {
      updated[page][action.action] = !allEnabled;
    });

    setLocalPermissions(updated);
    onChange({ permissions: updated, flags: localFlags, tenantAccess: localTenantAccess });
  };

  const tenantAccessDisabledByFlags = schemaFlags.some(
    (f) => f.ui?.disablesTenantAccessList && localFlags[f.key] === true
  );

  return (
    <div className="space-y-6">
      {/* Global Flags */}
      <Card>
        <CardHeader>
          <CardTitle>Global Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoadingSchema ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">Loading permission schema...</div>
          ) : schemaFlags.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">No global flags defined in permission schema.</div>
          ) : (
            schemaFlags.map((flag) => {
              const enabled = localFlags[flag.key] === true;
              return (
                <label key={flag.key} className="flex items-start gap-3 cursor-pointer">
                  <div
                    className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      enabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onClick={() => handleFlagToggle(flag.key)}
                  >
                    {enabled && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white">{flag.label}</div>
                    {(flag.description || flag.key) && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {flag.description || flag.key}
                      </div>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Page Permissions */}
      <Card>
        <CardHeader>
          <CardTitle>Page-Specific Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingSchema ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">Loading permission schema...</div>
          ) : pagePermissions.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">No permissions available. Check backend permission schema.</div>
          ) : (
            pagePermissions.map((pageConfig) => (
            <div key={pageConfig.page} className="border dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white">{pageConfig.displayName}</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageAllToggle(pageConfig.page)}
                >
                  Toggle All
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {pageConfig.actions.map((action) => (
                  <label
                    key={action.action}
                    className="flex items-start gap-2 cursor-pointer"
                  >
                    <div
                      className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        localPermissions[pageConfig.page]?.[action.action]
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      onClick={() => handlePermissionToggle(pageConfig.page, action.action)}
                    >
                      {localPermissions[pageConfig.page]?.[action.action] && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {action.label}
                      </div>
                      {action.description && (
                        <div className="text-gray-600 dark:text-gray-400 text-xs">
                          {action.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Tenant Access */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Access</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {tenantAccessDisabledByFlags && (
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Tenant access list is disabled by a global flag above. Disable it to assign specific tenants to this group.
            </div>
          )}

          {availableTenants.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              No tenants loaded.
            </div>
          ) : (
            <div className={cn("grid grid-cols-2 gap-3", tenantAccessDisabledByFlags && "opacity-60")}>
              {availableTenants.map((tenant) => {
                const checked = tenantAccessDisabledByFlags ? true : localTenantAccess.includes(tenant);
                return (
                  <label
                    key={tenant}
                    className={cn("flex items-center gap-2", tenantAccessDisabledByFlags ? "cursor-not-allowed" : "cursor-pointer")}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded border-2 flex items-center justify-center",
                        checked ? "bg-blue-600 border-blue-600" : "border-gray-300 dark:border-gray-600"
                      )}
                      onClick={() => {
                        if (tenantAccessDisabledByFlags) return;
                        handleTenantToggle(tenant);
                      }}
                    >
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm text-gray-900 dark:text-white">{tenant}</span>
                  </label>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

